// noinspection GraphQLUnresolvedReference

import type { GraphqlClientOptionsProps } from '../../../../base/api/graphqlClient.ts'
import { graphqlClient, toGraphQlError } from '../../../../base/api/graphqlClient.ts'
import { invalidateQuery, type InvalidateQueryOptions, useApiQuery } from '../../../../base/api/queryUtils.ts'
import type { QueryOpts } from '../../../../base/types/reactQuery.ts'
import { gqlAst } from '../../../../utils/gql.ts'

const query = /* GraphQL */ gqlAst`
  query LOGIN_ISI_PRINT {
    loginIsiPrint {
      token
      autorizado
      estado
      nivel
      mensaje
      tokenExpiraEn
      fechaVencimiento
      diasRestantes
      maximoConexiones
    }
  }
`

export const KEY_LOGIN_ISI_PRINT = 'login-isi-print'

/** Autorización de la sesión actual para usar isi-print (AdePrint). */
export interface ApiLoginIsiPrintResponse {
  /** Token para la app local de impresión; null si no está autorizado */
  token: string | null
  /** Si la sesión puede usar isi-print */
  autorizado: boolean
  /** Estado de la licencia */
  estado: string
  /** Nivel de la licencia */
  nivel: string
  /** Mensaje informativo (ej. motivo de no autorización o aviso de vencimiento) */
  mensaje: string | null
  /** Vencimiento del token */
  tokenExpiraEn: string | null
  /** Vencimiento de la licencia */
  fechaVencimiento: string | null
  /** Días que faltan para el vencimiento de la licencia */
  diasRestantes: number | null
  /** Conexiones simultáneas permitidas por la licencia */
  maximoConexiones: number | null
}

/**
 * Obtiene el token y el estado de la licencia de isi-print para la sesión actual.
 * @param options Headers adicionales y señal de cancelación de la petición.
 * @author isi-template
 */
export const apiLoginIsiPrint = async (
  options: GraphqlClientOptionsProps = {},
): Promise<ApiLoginIsiPrintResponse> => {
  try {
    const data = await graphqlClient.request<{ loginIsiPrint: ApiLoginIsiPrintResponse }>({
      document: query,
      requestHeaders: options.headers,
      signal: options.signal,
    })
    return data.loginIsiPrint
  } catch (error) {
    throw toGraphQlError(error, options.signal)
  }
}

/**
 * Invalida el login de isi-print desde cualquier lugar (ej. al cambiar de sesión o
 * de licencia), sin necesidad de tener montado el hook.
 * @example invalidateLoginIsiPrint()
 * @author isi-template
 */
export const invalidateLoginIsiPrint = (options?: InvalidateQueryOptions) =>
  invalidateQuery(KEY_LOGIN_ISI_PRINT, options)

/**
 * Hook para obtener el token y el estado de la licencia de isi-print.
 * queryKey: [KEY_LOGIN_ISI_PRINT, ...(options.queryKeys ?? [])]
 * No reintenta ante errores (autorización) ni se refresca al volver a la ventana.
 * Para invalidar sin montar el hook, usar `invalidateLoginIsiPrint()`.
 * @param options Opciones de React Query con soporte de queryKeys adicionales.
 * @author isi-template
 */
export const useLoginIsiPrint = (options: QueryOpts<ApiLoginIsiPrintResponse> = {}) =>
  useApiQuery(KEY_LOGIN_ISI_PRINT, (ctx) => apiLoginIsiPrint({ signal: ctx.signal }), {
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  })
